import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, TextField, Button, MenuItem, Select, FormControl, InputLabel, CardMedia, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import { 
  ClassicEditor,
	AccessibilityHelp,
	Autoformat,
	AutoImage,
	Autosave,
	BlockQuote,
	Bold,
	CloudServices,
	Essentials,
	Heading,
	ImageBlock,
	ImageCaption,
	ImageInline,
	ImageInsert,
	ImageInsertViaUrl,
	ImageResize,
	ImageStyle,
	ImageTextAlternative,
	ImageToolbar,
	ImageUpload,
	Indent,
	IndentBlock,
	Italic,
	Link,
	LinkImage,
	List,
	ListProperties,
	MediaEmbed,
	Paragraph,
	PasteFromOffice,
	SelectAll,
	SimpleUploadAdapter,
	Table,
	TableCaption,
	TableCellProperties,
	TableColumnResize,
	TableProperties,
	TableToolbar,
	TextTransformation,
	TodoList,
	Underline,
	Undo
 } from 'ckeditor5';
import 'ckeditor5/ckeditor5.css';
import '../App.css';
import { primaryColor } from '../theme';
import { useNavigate } from 'react-router-dom';
import { ArrowBack } from '@mui/icons-material';
import axios from 'axios';

const targetUsers = [
  { value: 'Staff', label: 'Staff' },
  { value: 'Farmer', label: 'Farmer' },
];

const targetPlatform = [
  { value: 'SMS', label: 'SMS' },
  { value: 'Audio', label: 'Audio' },
  { value: 'Video', label: 'Video' },
  { value: 'Written', label: 'Written' },
  { value: 'Whatsapp', label: 'Whatsapp' },
  { value: 'Facebook', label: 'Facebook' },
  { value: 'Presentations', label: 'Presentations' }
];

const UploadContentPage = () => {
  const [newContent, setNewContent] = useState({
    topic: '',
    targetUser: '',
    valueChain: '',
    platform: '',
    content: '',
    image: '',
    summary: '',
    video: null,
    audio: null,
    presentation: null
  });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [userId, setUserId] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (user) {
      setUserId(user.id);
    }
  }, []);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setNewContent({ ...newContent, [name]: value });
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    setNewContent({ ...newContent, [name]: files[0] });
  };

  const handleContentChange = (event, editor) => {
    const data = editor.getData();
    setNewContent({ ...newContent, content: data });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!newContent.topic) newErrors.topic = 'Topic is required';
    if (!newContent.targetUser) newErrors.targetUser = 'Target User is required';
    if (!newContent.valueChain) newErrors.valueChain = 'Value Chain is required';
    if (!newContent.platform) newErrors.platform = 'Platform is required';
    if (newContent.platform === 'SMS' && !newContent.content) newErrors.content = 'Content is required';
    if (newContent.platform === 'Written' && !newContent.content) newErrors.content = 'Content is required';
    if (newContent.platform === 'Whatsapp' && !newContent.content) newErrors.content = 'Content is required';
    if (newContent.platform === 'Facebook' && !newContent.content) newErrors.content = 'Content is required';
    if (newContent.platform === 'Video' && !newContent.video) newErrors.video = 'Video is required';
    if (newContent.platform === 'Audio' && !newContent.audio) newErrors.audio = 'Audio is required';
    if (newContent.platform === 'Presentation' && !newContent.presentation) newErrors.presentation = 'Presentation is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const formData = new FormData();
    formData.append('topic', newContent.topic);
    formData.append('targetUser', newContent.targetUser);
    formData.append('valueChain', newContent.valueChain);
    formData.append('platform', newContent.platform);
    formData.append('content', newContent.content);
    formData.append('summary', newContent.summary);
    formData.append('image', newContent.image);
    formData.append('video', newContent.video);
    formData.append('audio', newContent.audio);
    formData.append('presentation', newContent.presentation);
    formData.append('userId', userId);

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/content`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      navigate('/editor/dashboard'); // Navigate to the dashboard after successful submission
    } catch (error) {
      console.error('Error submitting content:', error);
    }
  };

  const handlePreviewOpen = () => {
    setPreviewOpen(true);
  };

  const handlePreviewClose = () => {
    setPreviewOpen(false);
  };

  const editorConfig = {
		toolbar: {
			items: [
				'undo',
				'redo',
				'|',
				'heading',
				'|',
				'bold',
				'italic',
				'underline',
				'|',
				'link',
				'insertImage',
				'mediaEmbed',
				'insertTable',
				'blockQuote',
				'|',
				'bulletedList',
				'numberedList',
				'todoList',
				'outdent',
				'indent'
			],
			shouldNotGroupWhenFull: false
		},
    simpleUpload: {
      uploadUrl: `${process.env.REACT_APP_API_BASE_URL}/upload`
    },
		plugins: [
			AccessibilityHelp,
			Autoformat,
			AutoImage,
			Autosave,
			BlockQuote,
			Bold,
			CloudServices,
			Essentials,
			Heading,
			ImageBlock,
			ImageCaption,
			ImageInline,
			ImageInsert,
			ImageInsertViaUrl,
			ImageResize,
			ImageStyle,
			ImageTextAlternative,
			ImageToolbar,
			ImageUpload,
			Indent,
			IndentBlock,
			Italic,
			Link,
			LinkImage,
			List,
			ListProperties,
			MediaEmbed,
			Paragraph,
			PasteFromOffice,
			SelectAll,
			SimpleUploadAdapter,
			Table,
			TableCaption,
			TableCellProperties,
			TableColumnResize,
			TableProperties,
			TableToolbar,
			TextTransformation,
			TodoList,
			Underline,
			Undo
		],
		heading: {
			options: [
				{
					model: 'paragraph',
					title: 'Paragraph',
					class: 'ck-heading_paragraph'
				},
				{
					model: 'heading1',
					view: 'h1',
					title: 'Heading 1',
					class: 'ck-heading_heading1'
				},
				{
					model: 'heading2',
					view: 'h2',
					title: 'Heading 2',
					class: 'ck-heading_heading2'
				},
				{
					model: 'heading3',
					view: 'h3',
					title: 'Heading 3',
					class: 'ck-heading_heading3'
				},
				{
					model: 'heading4',
					view: 'h4',
					title: 'Heading 4',
					class: 'ck-heading_heading4'
				},
				{
					model: 'heading5',
					view: 'h5',
					title: 'Heading 5',
					class: 'ck-heading_heading5'
				},
				{
					model: 'heading6',
					view: 'h6',
					title: 'Heading 6',
					class: 'ck-heading_heading6'
				}
			]
		},
		image: {
			toolbar: [
				'toggleImageCaption',
				'imageTextAlternative',
				'|',
				'imageStyle:inline',
				'imageStyle:wrapText',
				'imageStyle:breakText',
				'|',
				'resizeImage'
			]
		},
		initialData: '',
		link: {
			addTargetToExternalLinks: true,
			defaultProtocol: 'https://',
			decorators: {
				toggleDownloadable: {
					mode: 'manual',
					label: 'Downloadable',
					attributes: {
						download: 'file'
					}
				}
			}
		},
		list: {
			properties: {
				styles: true,
				startIndex: true,
				reversed: true
			}
		},
		placeholder: 'Type or paste your content here!',
		table: {
			contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells', 'tableProperties', 'tableCellProperties']
		}
	};

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>
        Go Back
      </Button>
      <Typography variant="h4" gutterBottom style={{ color: primaryColor }}>
        Upload New Content
      </Typography>
      <TextField
        label="Topic"
        name="topic"
        value={newContent.topic}
        onChange={handleInputChange}
        fullWidth
        margin="normal"
        error={!!errors.topic}
        helperText={errors.topic}
      />
      <FormControl fullWidth margin="normal" error={!!errors.targetUser}>
        <InputLabel>Target User</InputLabel>
        <Select
          name="targetUser"
          value={newContent.targetUser}
          label="Target User"
          onChange={handleInputChange}
        >
          {targetUsers.map((user) => (
            <MenuItem key={user.value} value={user.value}>
              {user.label}
            </MenuItem>
          ))}
        </Select>
        {errors.targetUser && <Typography color="error">{errors.targetUser}</Typography>}
      </FormControl>
      <TextField
        label="Value Chain"
        name="valueChain"
        value={newContent.valueChain}
        onChange={handleInputChange}
        fullWidth
        margin="normal"
        error={!!errors.valueChain}
        helperText={errors.valueChain}
      />
      <FormControl fullWidth margin="normal" error={!!errors.platform}>
        <InputLabel>Platform</InputLabel>
        <Select
          name="platform"
          value={newContent.platform}
          label="Platform"
          onChange={handleInputChange}
        >
          {targetPlatform.map((user) => (
            <MenuItem key={user.value} value={user.value}>
              {user.label}
            </MenuItem>
          ))}
        </Select>
        {errors.platform && <Typography color="error">{errors.platform}</Typography>}
      </FormControl>
      {(newContent.platform === 'Written' || newContent.platform === 'Presentations' || newContent.platform === 'Facebook') && (
        <>
          <TextField
            label="Preview Image URL"
            name="image"
            value={newContent.image}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
            error={!!errors.image}
            helperText={errors.image}
          />
          <input
            type="file"
            name="image"
            onChange={handleFileChange}
          />
        </>
      )}
      {newContent.platform === 'Video' && (
        <Box mt={2}>
          <TextField
            label="Video"
            name="video"
            type="file"
            onChange={handleFileChange}
            fullWidth
            margin="normal"
            error={!!errors.video}
            helperText={errors.video}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      )}
      {newContent.platform === 'Audio' && (
        <Box mt={2}>
          <TextField
            label="Audio"
            name="audio"
            type="file"
            onChange={handleFileChange}
            fullWidth
            margin="normal"
            error={!!errors.audio}
            helperText={errors.audio}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      )}
      {newContent.platform === 'Presentations' && (
        <Box mt={2}>
          <TextField
            label="Presentation"
            name="presentation"
            type="file"
            onChange={handleFileChange}
            fullWidth
            margin="normal"
            error={!!errors.presentation}
            helperText={errors.presentation}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      )}
      {(newContent.platform === 'Presentations' || newContent.platform === 'Written') && (
        <TextField
          label="Summary"
          name="summary"
          value={newContent.summary}
          onChange={handleInputChange}
          fullWidth
          margin="normal"
          multiline
          rows={2}
          error={!!errors.summary}
          helperText={errors.summary}
        />
      )}
      {(newContent.platform !== 'Audio' && newContent.platform !== 'Video' && newContent.platform !== 'Presentations') && (
      <Typography variant="body1" gutterBottom>
        Content
      </Typography>
      )}
      {(newContent.platform !== 'Audio' && newContent.platform !== 'Video' && newContent.platform !== 'Presentations') && (
        <CKEditor editor={ClassicEditor} data={newContent.content} onChange={handleContentChange} config={editorConfig} />
      )}
      {errors.content && <Typography color="error">{errors.content}</Typography>}
      <Box mt={2} mb={2} display="flex" justifyContent="space-between">
        <Button variant="contained" color="primary" onClick={handlePreviewOpen} sx={{ backgroundColor: primaryColor }}>
          Preview
        </Button>
        <Button variant="contained" color="primary" onClick={handleSubmit} sx={{ backgroundColor: primaryColor }}>
          Submit
        </Button>
      </Box>

      <Dialog open={previewOpen} onClose={handlePreviewClose} maxWidth="md" fullWidth>
        <DialogTitle>Content Preview</DialogTitle>
        <DialogContent>
          <Typography variant="h6">{newContent.topic}</Typography>
          <Typography variant="subtitle1">Target User: {newContent.targetUser}</Typography>
          <Typography variant="subtitle1">Value Chain: {newContent.valueChain}</Typography>
          <Typography variant="subtitle1">Platform: {newContent.platform}</Typography>
          <Typography variant="body1" dangerouslySetInnerHTML={{ __html: newContent.content }} />
          {newContent.image && (
            <Box mt={2}>
              <CardMedia
                component="img"
                height="200"
                image={newContent.image}
                alt={newContent.topic}
              />
            </Box>
          )}
          {newContent.video && (
            <Box mt={2}>
              <CardMedia
                component="video"
                controls
                height="200"
                src={URL.createObjectURL(newContent.video)}
                title={newContent.topic}
              />
            </Box>
          )}
          {newContent.audio && (
            <Box mt={2}>
              <audio controls>
                <source src={URL.createObjectURL(newContent.audio)} type="audio/mpeg" />
                Your browser does not support the audio element.
              </audio>
            </Box>
          )}
          {newContent.presentation && (
            <Box mt={2}>
              <iframe
                src={URL.createObjectURL(newContent.presentation)}
                width="100%"
                height="400"
                title={newContent.topic}
                frameBorder="0"
                allowFullScreen
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePreviewClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UploadContentPage;